import { useEffect, useRef, useState } from "react";
import type { RowData } from "@tanstack/react-table";
import { Check, Pencil, X } from "lucide-react";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/** How a cell edits. Drives which control the editor renders. */
export type EditKind = "text" | "number" | "date" | "select" | "textarea";

export interface EditOption {
  value: string;
  label: string;
}

/**
 * Declares a grid column as editable in place (AB#7874). Attach it to a
 * column's `meta.edit`; `DataTable` picks it up and wraps the cell.
 */
export interface ColumnEditMeta<TData> {
  /** Payload key the server matches against its whitelist. */
  field: string;
  kind: EditKind;
  /** Current value as the editor wants it — ISO `yyyy-MM-dd` for dates, the
   *  option value for selects, plain text otherwise. */
  value: (row: TData) => string;
  /** Required for `kind: "select"`. */
  options?: EditOption[];
  /** Blocks editing for a specific row (e.g. a closed deal's dates). */
  disabled?: (row: TData) => boolean;
}

/** The sentinel Radix needs because `<SelectItem value="">` is illegal. */
const CLEAR = "__clear__";

/**
 * How wide the editor floats while a cell is being edited (AB#8257).
 *
 * A grid column is sized for its truncated *display* value — `Account #` is
 * 160px — which left a user editing a 20-digit utility account number looking
 * at the last four characters. The editor therefore overlays its neighbours at
 * a width a value can actually be verified at, and the cell keeps its column
 * width so nothing reflows.
 */
const EDITOR_WIDTH = 340;

/** Same, for the multi-line editor: notes need the room more, not less. */
const TEXTAREA_EDITOR_WIDTH = 420;

/**
 * Floating shell the editing controls sit in.
 *
 * Absolutely positioned so widening it never reflows the fixed-layout table.
 * It anchors to the cell's left edge, and flips to the right edge when there
 * is not enough room before the scroll container's edge — otherwise editing a
 * column near the right of a wide grid would open the editor off-screen.
 *
 * When neither edge leaves room for the full width — a phone, where the grid
 * collapses into cards narrower than the editor — the shell shrinks to the
 * space it has instead of running off the screen.
 */
function EditorShell({
  width,
  children,
}: {
  width: number;
  children: React.ReactNode;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState({ alignRight: false, width });

  useEffect(() => {
    const node = anchorRef.current;
    if (!node) return;
    // The nearest ancestor that cuts off horizontal overflow — the grid's
    // scroll wrapper, or a phone card that clips its content — bounds the
    // shell; fall back to the viewport when there isn't one.
    let scroller: HTMLElement | null = node.parentElement;
    while (scroller) {
      const ox = getComputedStyle(scroller).overflowX;
      if (ox !== "visible") break;
      scroller = scroller.parentElement;
    }
    const bounds = scroller
      ? scroller.getBoundingClientRect()
      : { left: 0, right: window.innerWidth };
    const cell = node.getBoundingClientRect();
    const roomRight = bounds.right - cell.left;
    const roomLeft = cell.right - bounds.left;
    const alignRight = roomRight < width && roomLeft > roomRight;
    setPlacement({
      alignRight,
      width: Math.min(width, alignRight ? roomLeft : roomRight),
    });
  }, [width]);

  return (
    <div ref={anchorRef} className="relative h-7">
      <div
        className={cn(
          "absolute top-1/2 z-30 flex -translate-y-1/2 items-center gap-1",
          "rounded-md border border-border bg-card p-1 shadow-card",
          placement.alignRight ? "right-0" : "left-0",
        )}
        style={{ width: placement.width }}
      >
        {children}
      </div>
    </div>
  );
}

// TanStack's `ColumnMeta` is an empty interface meant for declaration
// merging — without this, a column def carrying `meta: { edit: … }` fails
// typecheck as an unknown property.
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Makes this column editable in place. See `ColumnEditMeta`. */
    edit?: ColumnEditMeta<TData>;
  }
}

/**
 * One editable grid cell. Reads as normal text until clicked, then swaps to
 * an input, a date field, or a select.
 *
 * Clicks are stopped from reaching the row so an edit never also navigates
 * to the record — the whole point of the ticket. Cells without an `edit`
 * descriptor keep the row's click, so the grid does not lose its
 * click-through.
 *
 * Enter and blur commit; Escape reverts. A rejected save restores the
 * previous text so the grid never shows a value the server did not take.
 * `onSave` reports its own failure to the user (a toast); its rejection only
 * tells the cell the value did not land.
 */
export function EditableCell<TData>({
  row,
  meta,
  onSave,
  children,
}: {
  row: TData;
  meta: ColumnEditMeta<TData>;
  onSave: (row: TData, field: string, value: string | null) => Promise<void>;
  children: React.ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const disabled = meta.disabled?.(row) ?? false;

  useEffect(() => {
    if (editing && meta.kind !== "select") inputRef.current?.focus();
  }, [editing, meta.kind]);

  const begin = (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    setDraft(meta.value(row));
    setEditing(true);
  };

  const commit = async (next: string) => {
    const previous = meta.value(row);
    setEditing(false);
    if (next === previous) return;
    setSaving(true);
    try {
      await onSave(row, meta.field, next === "" ? null : next);
    } catch {
      // `onSave` reports its own failure (every grid shows a toast with the
      // server's message) and rejects only to say the value did not land.
      // The cell already reads the row's stored value again, so nothing is
      // left to do here. Letting the rejection go on would surface as an
      // uncaught error from the blur or Enter handler that called commit.
    } finally {
      setSaving(false);
    }
  };

  if (disabled) {
    return <>{children}</>;
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={begin}
        aria-label="Edit"
        className={cn(
          "group/cell -mx-1 flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left",
          "hover:bg-row-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          saving && "opacity-60",
        )}
      >
        {/* A date is short and fixed-length, and a cut one ("2027-01…") is
            useless: it never shrinks below its own width, so an auto-layout
            table or a phone card sizes the cell around it. Free text keeps
            truncating. */}
        <span
          className={
            meta.kind === "date"
              ? "shrink-0 whitespace-nowrap"
              : "min-w-0 flex-1 truncate"
          }
        >
          {children}
        </span>
        {/* Out of the layout until hover or keyboard focus: a hidden icon
            that still took its width cut a date short ("2027-01…") in a
            column, or in a phone card, sized to fit the value alone. */}
        <Pencil
          size={11}
          className="hidden shrink-0 text-text-dim group-hover/cell:inline group-focus-visible/cell:inline"
        />
      </button>
    );
  }

  if (meta.kind === "select") {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <EditorShell width={EDITOR_WIDTH}>
          <Select
            defaultOpen
            value={draft === "" ? CLEAR : draft}
            onValueChange={(v) => void commit(v === CLEAR ? "" : v)}
            onOpenChange={(open) => {
              if (!open) setEditing(false);
            }}
          >
            <SelectTrigger className="h-7 flex-1 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={CLEAR}>—</SelectItem>
              {(meta.options ?? []).map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </EditorShell>
      </div>
    );
  }

  if (meta.kind === "textarea") {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <EditorShell width={TEXTAREA_EDITOR_WIDTH}>
          <Textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => void commit(draft)}
            onKeyDown={(e) => {
              // Enter inserts a newline; Cmd/Ctrl+Enter commits.
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void commit(draft);
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setEditing(false);
              }
            }}
            rows={3}
            className="min-h-16 flex-1 text-sm"
          />
          <X
            size={12}
            className="mt-1 shrink-0 cursor-pointer text-text-dim"
            onMouseDown={(e) => {
              // mousedown, not click — blur would commit first otherwise.
              e.preventDefault();
              setEditing(false);
            }}
          />
        </EditorShell>
      </div>
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <EditorShell width={EDITOR_WIDTH}>
        <Input
          ref={inputRef}
          type={
            meta.kind === "number"
              ? "number"
              : meta.kind === "date"
                ? "date"
                : "text"
          }
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void commit(draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void commit(draft);
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setEditing(false);
            }
          }}
          className="h-7 flex-1 text-sm"
        />
        <Check size={12} className="shrink-0 text-text-dim" aria-hidden />
        <X
          size={12}
          className="shrink-0 cursor-pointer text-text-dim"
          onMouseDown={(e) => {
            // mousedown, not click — blur would commit first otherwise.
            e.preventDefault();
            setEditing(false);
          }}
        />
      </EditorShell>
    </div>
  );
}
